import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import InputNumber from 'shared/ui/Input/InputNumber/InputNumber';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import Select from 'shared/ui/Select/Select';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import {TagsFilterField, translateMonitorStatus} from 'entities/monitor';
import {HttpMonitorStatus} from 'features/http/model/HttpMonitor/IHttpMonitor';
import IOrderBy from 'shared/model/IOrderBy';
import {booleanToString} from 'shared/lib/filterHelper';
import {useSort} from 'shared/lib/hooks/useSort';
import {
    getHttpMonitorsFilters,
    HTTP_MONITORS_DEFAULT_SORT,
    HTTP_MONITORS_ORDER_FIELDS,
} from 'features/http/lib/httpFilters';

import styles from './HttpMonitorFiltersForm.module.scss';

interface IHttpMonitorFiltersFormProps {
    onSubmit: SubmitHandler<IHttpMonitorFiltersForm>;
    button: React.ReactElement
}

export interface IHttpMonitorFiltersForm {
    name?: string,
    url?: string,
    tags?: string[],
    status?: HttpMonitorStatus,
    ok?: string,
    days?: number,
    orderBy?: IOrderBy,
}

const HttpMonitorFiltersForm: React.FC<IHttpMonitorFiltersFormProps> = ({onSubmit, button}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = getHttpMonitorsFilters(searchParams);
    const [sort] = useSort({defaultSort: HTTP_MONITORS_DEFAULT_SORT, sortFields: HTTP_MONITORS_ORDER_FIELDS});

    const {register, handleSubmit, control, setValue} = useForm<IHttpMonitorFiltersForm>({
        mode: 'onSubmit',
        defaultValues: {
            status: filters.status,
            ok: booleanToString(filters.ok),
            days: filters.days,
            tags: filters.tags || [],
        },
    });

    const statusOptions = ['active', 'paused', 'unsubscribed']
        .map(status => ({
            label: translateMonitorStatus(status as HttpMonitorStatus),
            value: status,
        }));

    const okOptions = [
        {
            label: `${t`Исправен`}`,
            value: 'true',
        },
        {
            label: `${t`Есть проблемы`}`,
            value: 'false',
        },
    ];

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    label={t`URL или его часть`}
                >
                    <InputText
                        register={register}
                        defaultValue={filters?.url}
                        name="url"
                        placeholder=""
                        control={control}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Название или его часть`}
                >
                    <InputText
                        defaultValue={filters?.name}
                        name="name"
                        placeholder=""
                        register={register}
                        control={control}
                    />
                </FormGroupWithError>

                <TagsFilterField control={control}/>

                <FormGroupWithError
                    label={t`Состояние`}
                >
                    <Select
                        control={control}
                        name="status"
                        data={statusOptions}
                        defaultValue={filters.status}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Исправность`}
                >
                    <Select
                        control={control}
                        name="ok"
                        data={okOptions}
                        defaultValue={
                            filters.ok === undefined
                                ? undefined
                                : String(filters.ok)
                        }
                        cleanable={true}
                        searchable={false}
                        onChange={(value) => {
                            if (value === null) {
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('ok');
                                setSearchParams(newParams);
                            }
                        }}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Количество дней для расчета доступности`}
                >
                    <InputNumber
                        defaultValue={filters.days}
                        min={1}
                        max={31}
                        name="days"
                        placeholder=""
                        control={control}
                        setValue={setValue}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Сортировка`}
                >
                    <OrderByControlled
                        control={control}
                        name="orderBy"
                        defaultValue={sort}
                        data={[
                            {label: `${t`Название`}`, value: 'name'},
                            {label: `${t`URL`}`, value: 'url'},
                            {label: `${t`Интервал, мин`}`, value: 'checkInterval'},
                            {label: `${t`Состояние`}`, value: 'status'},
                            {label: `${t`Последний результат`}`, value: 'lastCode'},
                        ]}
                    />
                </FormGroupWithError>

            </ModalBodyWrapper>
            <FormGroup>
                {button}
            </FormGroup>
        </form>
    );
};

export default HttpMonitorFiltersForm;